import React, { useEffect, useState, useCallback } from "react";
import VisorPluginPlaceholder from "../../_visor/components/canvas/VisorPluginPlaceholder";
import VisorSortableList from "../../_visor/components/common/VisorSortableList";
import QuizStatusLine from "../../_visor/components/canvas/QuizStatusLine";
import { contentI18n as i18n } from "../../common/content_language";
import { setRgbaAlpha, shuffle, getButtonText } from "../../common/common_tools";

/* eslint-disable react/prop-types */

const generateRandomPositions = (N) => {
  let a = [];
  for (let i = 0; i < N; ++i) {
    a[i] = i;
  }
  return shuffle(a);
};

const OrderVisor = (wrapperProps) => {
  const { state, props } = wrapperProps;

  const [positions, setPositions] = useState(() => generateRandomPositions(state.nBoxes));

  const attempted = props.exercises && props.exercises.attempted;

  const refreshAnswers = useCallback(() => {
    const position = generateRandomPositions(state.nBoxes);
    setPositions(position);
    props.setAnswer(position);
  }, [state.nBoxes, props]);

  // Seed the initial (shuffled) order as the current answer once on mount, so a
  // learner who submits without reordering is still scored against what they
  // see. Reordering now goes through dnd-kit (VisorSortableList) instead of the
  // old jQuery-UI sortable + 50ms-timeout init.
  useEffect(() => {
    if (!attempted) props.setAnswer(positions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render logic
  let score = props.exercises.score || 0;
  score = Math.round(score * 100) / 100;
  score =
    props.exercises.weight === 0
      ? i18n.t("Ordering.notCount")
      : score + "/" + props.exercises.weight;
  let showFeedback = attempted && state.showFeedback;

  let istrue =
    attempted &&
    Array.isArray(props.exercises.currentAnswer) &&
    JSON.stringify([...props.exercises.currentAnswer].sort()) ==
      JSON.stringify(props.exercises.currentAnswer);

  const isTryAgainMode =
    state.buttons === "tryAgain" ||
    state.buttons === i18n.t("MultipleChoice.tryAgain") ||
    state.buttons === "Try Again" ||
    state.buttons === "Tekrar Dene";
  let quizColor = state.buttonBackgroundColor || state.quizColor || "rgba(237, 233, 254, 1)";
  let buttonStyles = {
    backgroundColor: state.buttonBackgroundColor || "#ede9fe",
    color: state.buttonTextColor || "#111827",
    borderColor: state.buttonBorderColor || "#ede9fe",
    borderWidth: `${state.buttonBorderWidth || 1}px`,
    borderRadius: `${state.buttonBorderRadius || 4}px`,
    borderStyle: "solid",
    padding: `${state.buttonPadding || 8}px ${(state.buttonPadding || 8) * 1.5}px`,
    fontSize: `${state.buttonFontSize || 14}px`,
    fontWeight: state.buttonFontWeight || "normal",
    cursor: "pointer",
    margin: "2px",
    transition: "background-color 0.2s ease",
  };
  let currentPositions =
    attempted && Array.isArray(props.exercises.currentAnswer)
      ? props.exercises.currentAnswer
      : positions;

  // Build the dnd-kit sortable items in display order. `id` is the answer index
  // (stable + unique); reordering the ids IS the new `positions` array. An item
  // is correct when its answer index equals its slot index (i === j).
  const sortItems = currentPositions.map((answerIdx, j) => ({
    id: answerIdx,
    content: (
      <VisorPluginPlaceholder
        {...props}
        key={answerIdx + 1}
        pluginContainer={"Answer" + answerIdx}
      />
    ),
    // Tekrar ("Try Again") modunda hiç renk verilmez (cevap gizli kalsın); sonuç
    // buton altındaki durum satırında. Diğer modlarda öğe bazında yeşil/kırmızı.
    status: attempted && !isTryAgainMode ? (answerIdx !== j ? "incorrect" : "correct") : null,
  }));

  let trueFeedbackContainer = props.boxes[props.id].sortableContainers["sc-TrueFeedback"];
  let trueFeedbackToolbar = trueFeedbackContainer?.children?.[0]
    ? props.toolbars[trueFeedbackContainer.children[0]]
    : null;

  let falseFeedbackContainer = props.boxes[props.id].sortableContainers["sc-FalseFeedback"];
  let falseFeedbackToolbar = falseFeedbackContainer?.children?.[0]
    ? props.toolbars[falseFeedbackContainer.children[0]]
    : null;

  const fixImgSvgStructure = (text) => {
    if (!text || typeof text !== "string") return text;
    let decoded = text;
    try {
      if (text.includes("%3C") || text.includes("%3E")) {
        decoded = decodeURIComponent(text);
      }
    } catch (e) {}

    decoded = decoded.replace(/<img\s*([^>]*?)>([\s\S]*?)<\/img>/gi, (match, attrs, content) => {
      if (content && content.trim() && content.includes("<svg")) {
        let spanAttrs = attrs ? attrs.trim() : "";
        if (spanAttrs && spanAttrs.includes("style=")) {
          spanAttrs = spanAttrs.replace(
            /style\s*=\s*["']([^"']*)["']/i,
            (styleMatch, styleContent) => {
              let cleanStyle = styleContent.trim().replace(/;\s*$/, "");
              return `style="${cleanStyle};display:inline-block;"`;
            }
          );
        } else {
          spanAttrs = (spanAttrs ? spanAttrs + " " : "") + 'style="display:inline-block;"';
        }
        return `<span ${spanAttrs}>${content}</span>`;
      }
      return match;
    });
    decoded = decoded.replace(/<img\s*([^>]*?)>\s*<svg/gi, (match, attrs) => {
      let spanAttrs = attrs ? attrs.trim() : "";
      if (spanAttrs && spanAttrs.includes("style=")) {
        spanAttrs = spanAttrs.replace(
          /style\s*=\s*["']([^"']*)["']/i,
          (styleMatch, styleContent) => {
            let cleanStyle = styleContent.trim().replace(/;\s*$/, "");
            return `style="${cleanStyle};display:inline-block;"`;
          }
        );
      } else {
        spanAttrs = (spanAttrs ? spanAttrs + " " : "") + 'style="display:inline-block;"';
      }
      return `<span ${spanAttrs}><svg`;
    });
    decoded = decoded.replace(/<\/svg>[\s\n]*<\/img>/gi, "</svg></span>");
    if (text.includes("%3C") || text.includes("%3E")) {
      try {
        return encodeURIComponent(decoded);
      } catch (e) {
        return decoded;
      }
    }
    return decoded;
  };

  let trueFeedbackText = fixImgSvgStructure(trueFeedbackToolbar?.state?.__text || "");
  let isDefaultTrueFeedbackMsg = false;

  let defaultTrueFeedbackMsg = "<p>" + i18n.t("MultipleChoice.TrueFeedbackMsg") + "</p>";

  if (
    trueFeedbackText === defaultTrueFeedbackMsg ||
    trueFeedbackText === encodeURI(defaultTrueFeedbackMsg) ||
    trueFeedbackText === encodeURI(defaultTrueFeedbackMsg + "\n") ||
    trueFeedbackText.includes(i18n.t("MultipleChoice.TrueFeedbackMsg"))
  ) {
    isDefaultTrueFeedbackMsg = true;
  }

  let checkEmptyTrueFeedback =
    (!trueFeedbackContainer?.children ||
      trueFeedbackContainer.children.length === 0 ||
      trueFeedbackToolbar?.state?.__text === "<p>" + i18n.t("text_here") + "</p>" ||
      trueFeedbackToolbar?.state?.__text === encodeURI("<p>" + i18n.t("text_here") + "</p>") ||
      trueFeedbackToolbar?.state?.__text === encodeURI("<p>" + i18n.t("text_here") + "</p>\n")) &&
    !isDefaultTrueFeedbackMsg;

  let falseFeedbackText = fixImgSvgStructure(falseFeedbackToolbar?.state?.__text || "");
  let isDefaultFalseFeedbackMsg = false;

  let defaultFalseFeedbackMsg = "<p>" + i18n.t("MultipleChoice.FalseFeedbackMsg") + "</p>";

  if (
    falseFeedbackText === defaultFalseFeedbackMsg ||
    falseFeedbackText === encodeURI(defaultFalseFeedbackMsg) ||
    falseFeedbackText === encodeURI(defaultFalseFeedbackMsg + "\n") ||
    falseFeedbackText.includes(i18n.t("MultipleChoice.FalseFeedbackMsg"))
  ) {
    isDefaultFalseFeedbackMsg = true;
  }

  let checkEmptyFalseFeedback =
    (!falseFeedbackContainer?.children ||
      falseFeedbackContainer.children.length === 0 ||
      falseFeedbackToolbar?.state?.__text === "<p>" + i18n.t("text_here") + "</p>" ||
      falseFeedbackToolbar?.state?.__text === encodeURI("<p>" + i18n.t("text_here") + "</p>") ||
      falseFeedbackToolbar?.state?.__text === encodeURI("<p>" + i18n.t("text_here") + "</p>\n")) &&
    !isDefaultFalseFeedbackMsg;

  return (
    <div
      className={
        "exercisePlugin orderingPlugin" +
        (attempted ? " attempted " : " ") +
        (isTryAgainMode ? " ordering-tryagain " : " ") +
        (props.exercises.showFeedback ? "showFeedback" : "")
      }
      style={{ "--q-btn-hover-bg": state.buttonHoverBackgroundColor || "#ede9fe" }}
    >
      <div className={"row"} key={0}>
        <div className={"col-xs-12"}>
          <VisorPluginPlaceholder {...props} key="0" pluginContainer={"Question"} />
        </div>
      </div>
      <VisorSortableList
        items={sortItems}
        disabled={!!attempted}
        ariaLabel={i18n.t("Ordering.PluginName") || "Ordering"}
        onReorder={(newOrder) => {
          setPositions(newOrder);
          props.setAnswer(newOrder);
        }}
      />
      {istrue && !checkEmptyTrueFeedback ? (
        <div
          className={"row feedbackRow"}
          key={-2}
          style={{ display: showFeedback ? "block" : "none" }}
        >
          <div
            className={"col-xs-12 feedback"}
            role="status"
            aria-live="polite"
            style={{
              color: "black",
              borderColor: "#318533",
              backgroundColor: setRgbaAlpha("rgba(39,208,124,0.20)", 0.15),
              display: "flex",
              alignItems: "center",
            }}
          >
            <VisorPluginPlaceholder {...props} key="0" pluginContainer={"TrueFeedback"} />
          </div>
        </div>
      ) : null}
      {!istrue && !checkEmptyFalseFeedback ? (
        <div
          className={"row feedbackRow"}
          key={-2}
          style={{ display: showFeedback ? "block" : "none" }}
        >
          <div
            className={"col-xs-12 feedback"}
            role="status"
            aria-live="polite"
            style={{
              color: "black",
              borderColor: "#a31111",
              backgroundColor: setRgbaAlpha("rgba(213,26,26,0.20)", 0.15),
              display: "flex",
              alignItems: "center",
            }}
          >
            <VisorPluginPlaceholder {...props} key="1232" pluginContainer={"FalseFeedback"} />
          </div>
        </div>
      ) : null}

      {isTryAgainMode && !state.showFeedback ? (
        <QuizStatusLine correct={istrue} visible={attempted} />
      ) : null}

      <div className={"exerciseScore"} style={{ color: quizColor }}>
        {score}
      </div>
      <div className={"row"} key={7}>
        <button
          type="button"
          className={"tryButton"}
          style={{
            display:
              istrue ||
              !props.exercises.attempted ||
              (state.buttons != "tryAgain" &&
                state.buttons != i18n.t("MultipleChoice.tryAgain") &&
                state.buttons != "Try Again" &&
                state.buttons != "Tekrar Dene")
                ? "none"
                : "inline-block",
            ...buttonStyles,
            height: "48px",
          }}
          onClick={() => {
            refreshAnswers();
            props.tryAgainHandler();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe";
          }}
        >
          {getButtonText(state.tryAgainText, "MultipleChoice.tryAgain")}
        </button>
        <button
          type="button"
          className={"tryButton"}
          style={{
            display:
              istrue ||
              !props.exercises.attempted ||
              (state.buttons != i18n.t("MultipleChoice.seeAnswer") && state.buttons != "seeAnswer")
                ? "none"
                : "inline-block",
            ...buttonStyles,
          }}
          onClick={() => props.seeAnswerHandler()}
          onMouseEnter={(e) =>
            (e.target.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe")
          }
          onMouseLeave={(e) =>
            (e.target.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe")
          }
        >
          {getButtonText(state.seeAnswerText, "MultipleChoice.seeAnswer")}
        </button>
        <button
          type="button"
          className={"tryButton"}
          style={{
            display: props.exercises.attempted ? "none" : "inline-block",
            ...buttonStyles,
            height: "48px",
          }}
          onClick={() => props.submitPage(props.currentView, props.id)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe";
          }}
        >
          {getButtonText(state.checkAnswerText, "MultipleChoice.checkAnswer")}
        </button>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .orderingPlugin input[type="radio"] {
              background-color: transparent;
            }
            .orderingPlugin input[type="radio"]:checked:after {
              background-color: ${quizColor};
            }
            /* tryAgain modunda tik/çarpı ikonu yok; sadece yeşil/kırmızı çerçeve. */
            .orderingPlugin.ordering-tryagain .vsl-status {
              display: none;
            }
          `,
        }}
      />
    </div>
  );
};

export default OrderVisor;

/* eslint-enable react/prop-types */
